import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from 'components/UI/Buttons/BaseButton';
import TextField from 'components/UI/Inputs/TextField';
import InputLabel from 'components/UI/Inputs/InputLabel';
import Select from 'components/UI/Inputs/Select';
import Checkbox from 'components/UI/Inputs/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import DueDateSelect from './components/DueDateSelect';
import DeleteDialog from 'components/UI/Dialogs/DeleteDialog';

import { useAppSelector } from 'src/app/hooks';
import { selectNotHiddenContacts } from 'src/slices/contactsSlice';
import { selectProposals } from 'src/slices/proposalsSlice';
import { selectProformaInvoices } from 'src/slices/proformaInvoicesSlice/proformaInvoicesSlice';
import { selectIsAdminOrOwner } from 'src/slices/workspacesSlice';
import SearchContactInput from 'src/features/contacts/SearchContactInput';
import {
  DISCOUNT_TYPES,
  PROFORMA_INVOICE_STATUS,
} from 'src/db/entities/proforma_invoice/ProformaInvoiceEntity';
import styles from './ProformaInvoiceForm.module.scss';
import { ProformaInvoiceFormProps, ProformaInvoiceFormValues } from './types';
import { calculateProformaInvoicePrice } from 'src/api/proformaInvoices/api';
import { MessageType } from 'components/UI/snackbar/providers/SnackBarProvider';
import { useSnackbar } from 'notistack';
import { ProformaInvoiceDraft } from 'src/types/proformaInvoice';
import { cloneSettingsForInvoice } from './mappers';
import {
  formatInvoiceNumbers,
  getInvoicedQuoteIds,
  getOtherInvoicesForQuote,
} from './invoicedQuotes';
import ProposalSettings from 'src/db/entities/proposalSettings/ProposalSettingsEntity';
import useSettingsSubForm, {
  FormValues as SettingsFormValues,
} from './subForms/SettingsSubForm/useSettingsSubForm';
import SettingsForm from './subForms/SettingsSubForm/Layout';

const defaultItems = () => [{ order: 1, description: '', qty: 1, amount: 0 }];

const applyPrecision = (value: number, decimals?: number) => {
  if (decimals == null) {
    return value;
  }

  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

const normalizeNumberInput = (value: unknown, fallback = 0, decimals?: number) => {
  const prepareFallback = () =>
    applyPrecision(typeof fallback === 'number' ? fallback : 0, decimals);

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return prepareFallback();
    }

    return applyPrecision(value, decimals);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return prepareFallback();
    }

    const parsed = Number(trimmed.replace(',', '.'));
    if (!Number.isFinite(parsed)) {
      return prepareFallback();
    }

    return applyPrecision(parsed, decimals);
  }

  return prepareFallback();
};

const validationSchema = (requiredContact: boolean, t: (key: string, opts?: any) => string) =>
  yup.object().shape({
    title: yup.string().nullable().required(t('form.validation.titleRequired')),
    contactId: requiredContact
      ? yup.string().nullable().required(t('form.validation.contactRequired'))
      : yup.string().nullable(),
    contactName: yup
      .string()
      .nullable()
      .required(t('form.validation.clientNameRequired'))
      .max(40, t('form.validation.clientNameTooLong')),
    items: yup
      .array()
      .of(
        yup.object({
          description: yup.string().nullable(),
          qty: yup
            .number()
            .typeError(t('form.validation.qtyNumber'))
            .min(0, t('form.validation.qtyPositive')),
          amount: yup
            .number()
            .typeError(t('form.validation.amountNumber'))
            .min(0, t('form.validation.amountPositive')),
        })
      )
      .min(1, t('form.validation.itemsRequired')),
    discountAmount: yup.number().nullable().min(0, t('form.validation.discountPositive')),
  });

interface Props extends ProformaInvoiceFormProps {
  onFormDataChanged: () => void;
}

const ensureItems = (items?: ProformaInvoiceDraft['items']) =>
  items && items.length > 0
    ? items.map((item, index) => ({
        order: item.order ?? index + 1,
        description: item.description ?? '',
        qty: normalizeNumberInput(item.qty, item.qty ?? 0, 4),
        amount: normalizeNumberInput(item.amount, 0, 2),
      }))
    : defaultItems();

const toSubmittedValues = (
  values: ProformaInvoiceFormValues,
  settings: ProposalSettings
): ProformaInvoiceFormValues => {
  const dueDateValue =
    typeof values.dueDate === 'number'
      ? values.dueDate
      : values.dueDate
      ? (values.dueDate as Date).getTime()
      : null;

  const itemsWithOrder = values.items.map((item, index) => ({
    ...item,
    order: index + 1,
    qty: normalizeNumberInput(item.qty, 0, 4),
    amount: normalizeNumberInput(item.amount, 0, 2),
  }));

  return {
    ...values,
    items: itemsWithOrder,
    discountAmount: normalizeNumberInput(values.discountAmount, 0, 2),
    dueDate: dueDateValue,
    createdAt: values.createdAt,
    updatedAt: Date.now(),
    settings,
  } as ProformaInvoiceFormValues;
};

export default function GenericForm({
  defaultValues,
  onSubmit,
  onCancel,
  onDelete,
  mode,
  onFormDataChanged,
}: Props) {
  const { t } = useTranslation('proforma-invoices-page');
  const { enqueueSnackbar } = useSnackbar();
  const contacts = useAppSelector(selectNotHiddenContacts);
  const proposals = useAppSelector(selectProposals);
  const invoices = useAppSelector(selectProformaInvoices);
  const isAdmin = useAppSelector(selectIsAdminOrOwner);

  const [currentSection, setCurrentSection] = useState<'main' | 'settings'>('main');
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);

  const formDefaults = useMemo<ProformaInvoiceFormValues>(() => {
    const isCreateMode = mode === 'create';

    const base: ProformaInvoiceFormValues = {
      ...defaultValues,
      items: ensureItems(defaultValues?.items),
      discountAmount: defaultValues?.discountAmount ?? 0,
      discountType: defaultValues?.discountType ?? DISCOUNT_TYPES.VALUE,
      priceProposalOverrideFields: isCreateMode
        ? false
        : defaultValues?.priceProposalOverrideFields ?? false,
      priceProposalOverrideSettings: isCreateMode
        ? false
        : defaultValues?.priceProposalOverrideSettings ?? false,
      status: defaultValues?.status ?? PROFORMA_INVOICE_STATUS.PENDING_PAYMENT,
      settings: cloneSettingsForInvoice(defaultValues?.settings),
    } as ProformaInvoiceFormValues;

    return base;
  }, [defaultValues, mode]);

  const methods = useForm<ProformaInvoiceFormValues>({
    defaultValues: formDefaults,
    resolver: yupResolver(validationSchema(true, t)),
    mode: 'onBlur',
  });

  const { control, handleSubmit, watch, setValue, getValues, formState, reset } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: 'items' });

  // Settings last saved in the settings sub-form (or the initial ones); used on submit.
  const initialSettingsRef = useRef(cloneSettingsForInvoice(defaultValues?.settings));
  const settingsForm = useSettingsSubForm(initialSettingsRef.current);
  const {
    watch: watchSettings,
    formState: settingsFormState,
    resetWithSettings,
    toEntity: toSettingsEntity,
  } = settingsForm;

  const defaultsKey = defaultValues?.uuid ?? 'create';

  useEffect(() => {
    const snapshot = cloneSettingsForInvoice(defaultValues?.settings);
    initialSettingsRef.current = snapshot;
    reset(formDefaults);
    resetWithSettings(snapshot);
  }, [defaultsKey, reset, resetWithSettings, formDefaults, defaultValues]);

  useEffect(() => {
    if (formState.isDirty || settingsFormState.isDirty) {
      onFormDataChanged();
    }
  }, [formState.isDirty, settingsFormState.isDirty, onFormDataChanged]);

  const items = useWatch({ control, name: 'items' });
  const priceProposalId = useWatch({ control, name: 'priceProposalId' });
  const hasLinkedQuote = !!priceProposalId;
  const existingQuoteInvoices = useMemo(
    () => getOtherInvoicesForQuote(invoices, priceProposalId, defaultValues?.uuid),
    [invoices, priceProposalId, defaultValues?.uuid]
  );
  const discountType = useWatch({ control, name: 'discountType' }) ?? DISCOUNT_TYPES.VALUE;
  const discountAmountRaw = useWatch({ control, name: 'discountAmount' });
  const discountAmount = useMemo(
    () => normalizeNumberInput(discountAmountRaw, 0, 2),
    [discountAmountRaw]
  );
  const includesTax = watchSettings('includesTax') ?? false;
  const taxRate = watchSettings('taxRate') ?? 0;

  const sanitizedItemsForCalculation = useMemo(() => {
    if (!items || items.length === 0) {
      return [] as ProformaInvoiceDraft['items'];
    }

    return items.map((item, idx) => ({
      order: item.order ?? idx + 1,
      description: item.description ?? '',
      qty: normalizeNumberInput(item.qty, 0, 4),
      amount: normalizeNumberInput(item.amount, 0, 2),
    }));
  }, [items]);

  const invoicedQuoteIds = useMemo(
    () => getInvoicedQuoteIds(invoices, defaultValues?.uuid),
    [invoices, defaultValues?.uuid]
  );

  const proposalOptions = useMemo(
    () => [
      {
        value: '',
        text: <em>{t('form.fields.none')}</em>,
      },
      ...proposals.map((proposal) => ({
        value: proposal.uuid,
        text: `${proposal.number ? `${proposal.number} — ${proposal.title}` : proposal.title}${
          invoicedQuoteIds.has(proposal.uuid) ? ` · ${t('form.fields.invoicedSuffix')}` : ''
        }`,
      })),
    ],
    [proposals, t, invoicedQuoteIds]
  );

  const statusOptions = useMemo(
    () => [
      {
        value: PROFORMA_INVOICE_STATUS.PENDING_PAYMENT,
        text: t('status_value.PENDING_PAYMENT'),
      },
      {
        value: PROFORMA_INVOICE_STATUS.PARTIALLY_PAID,
        text: t('status_value.PARTIALLY_PAID'),
      },
      {
        value: PROFORMA_INVOICE_STATUS.FULLY_PAID,
        text: t('status_value.FULLY_PAID'),
      },
    ],
    [t]
  );

  const discountOptions = useMemo(
    () => [
      { value: DISCOUNT_TYPES.VALUE, text: t('form.discount.value') },
      { value: DISCOUNT_TYPES.PERCENT, text: t('form.discount.percent') },
    ],
    [t]
  );

  const priceProposalFieldId = 'proforma_invoice_form_price_proposal';
  const discountTypeFieldId = 'proforma_invoice_form_discount_type';
  const statusFieldId = 'proforma_invoice_form_status';

  const getProposalLabel = useCallback(
    (value: string | null | undefined) => {
      if (!value) return t('form.fields.priceProposalPlaceholder');
      const proposal = proposals.find((p) => p.uuid === value);
      if (!proposal) return value;
      return proposal.number ? `${proposal.number} — ${proposal.title}` : proposal.title;
    },
    [proposals, t]
  );

  const itemsSignature = useMemo(
    () => JSON.stringify(sanitizedItemsForCalculation),
    [sanitizedItemsForCalculation]
  );

  const pricingSignature = JSON.stringify([
    itemsSignature,
    discountType,
    discountAmount,
    includesTax,
    taxRate,
  ]);
  const initialPricingSignature = useRef(pricingSignature).current;

  useEffect(() => {
    let ignore = false;

    (async () => {
      const updateIfChanged = (path: keyof ProformaInvoiceFormValues, value: number | null) => {
        const current = getValues(path) as number | null;
        if (
          current === value ||
          (current != null && value != null && Math.abs(current - value) < 0.0001)
        ) {
          return;
        }
        setValue(path, value, { shouldDirty: false, shouldValidate: false });
      };

      // Show the totals the backend computed until the user changes a pricing input.
      // Older invoices have no items, so recalculating would show zero totals.
      if (mode === 'update' && pricingSignature === initialPricingSignature) {
        updateIfChanged('subtotalPrice', defaultValues?.subtotalPrice ?? null);
        updateIfChanged('priceWithDiscount', defaultValues?.priceWithDiscount ?? null);
        updateIfChanged('priceWithTax', defaultValues?.priceWithTax ?? null);
        updateIfChanged('taxValue', defaultValues?.taxValue ?? null);
        return;
      }

      if (sanitizedItemsForCalculation.length === 0) {
        updateIfChanged('subtotalPrice', null);
        updateIfChanged('priceWithDiscount', null);
        updateIfChanged('priceWithTax', null);
        updateIfChanged('taxValue', null);
        return;
      }

      try {
        const result = await calculateProformaInvoicePrice({
          items: sanitizedItemsForCalculation,
          discountType,
          discountAmount,
          includesTax,
          taxRate,
        });

        if (!ignore) {
          updateIfChanged('subtotalPrice', result.subtotal);
          updateIfChanged('priceWithDiscount', result.subtotalWithDiscount);
          updateIfChanged('priceWithTax', result.total);
          updateIfChanged('taxValue', result.tax);
        }
      } catch (error) {
        enqueueSnackbar({
          message: t('form.priceCalculationError'),
          variant: MessageType.Error,
        });
      }
    })();

    return () => {
      ignore = true;
    };
  }, [
    itemsSignature,
    discountType,
    discountAmount,
    includesTax,
    taxRate,
    enqueueSnackbar,
    setValue,
    t,
    getValues,
    sanitizedItemsForCalculation,
    mode,
    pricingSignature,
    initialPricingSignature,
    defaultValues,
  ]);

  const onSubmitInternal = async (values: ProformaInvoiceFormValues) => {
    await onSubmit(
      toSubmittedValues(values, initialSettingsRef.current),
      toSubmittedValues(formDefaults, cloneSettingsForInvoice(defaultValues?.settings))
    );
  };

  const renderMainForm = (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitInternal)} className={styles.formWrapper}>
        <section className={styles.section}>
          <div className={styles.sectionHeaderRow}>
            <Typography variant="h6" className={styles.sectionTitle}>
              {t('form.sections.main')}
            </Typography>
            <Button
              variant="text"
              onClick={() => setCurrentSection('settings')}
              className={styles.settingsButton}
            >
              {t('form.actions.openSettings')}
            </Button>
          </div>

          <Box mb={2}>
            <Typography variant="body2" className={styles.note}>
              {t('form.notes.pdfRegenerate')}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="title"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('form.fields.title')}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="contactName"
                control={control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    label={t('form.fields.client')}
                    fullWidth
                    inputProps={{ maxLength: 40 }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="priceProposalId"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <InputLabel htmlFor={priceProposalFieldId}>
                      {t('form.fields.priceProposal')}
                    </InputLabel>
                    <Select
                      {...field}
                      id={priceProposalFieldId}
                      value={field.value ?? ''}
                      options={proposalOptions}
                      fullWidth
                      renderVal={getProposalLabel(field.value as string | null | undefined)}
                      displayEmpty
                    />
                  </FormControl>
                )}
              />
              {existingQuoteInvoices.length > 0 && (
                <Typography
                  variant="body2"
                  role="alert"
                  className={clsx(styles.note, styles.noteSpacing, styles.warningNote)}
                >
                  {t('form.notes.quoteAlreadyInvoiced', {
                    numbers: formatInvoiceNumbers(existingQuoteInvoices),
                  })}
                </Typography>
              )}
              <Typography variant="body2" className={clsx(styles.note, styles.noteSpacing)}>
                {t('form.notes.priceProposalMessage')}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                {t('form.fields.priceProposalReference')}
              </Typography>
              <div className={styles.referenceDisplay}>
                {watch('priceProposalReference') ?? '-'}
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" className={styles.fieldLabel}>
                {t('form.fields.invoiceId')}
              </Typography>
              <div className={styles.referenceDisplay}>{watch('invoiceId') ?? '-'}</div>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="contactId"
                control={control}
                render={({ field, fieldState }) => (
                  <SearchContactInput
                    contacts={contacts}
                    id="proforma_invoice_contact"
                    label={t('form.fields.contact')}
                    placeholder={t('form.fields.contact')}
                    value={field.value ?? ''}
                    onChange={(contactId) => {
                      field.onChange(contactId);
                      const selectedContact = contacts.find((c) => c.uuid === contactId);
                      // Keep a Client name the user already typed; only fill it when empty.
                      if (selectedContact && !getValues('contactName')?.trim()) {
                        const fullName =
                          selectedContact.suggestion_name || selectedContact.name || '';
                        setValue('contactName', fullName);
                      }
                    }}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
              <Typography variant="body2" className={styles.note}>
                {t('form.notes.overrideContactWarning')}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('form.fields.description')}
                    fullWidth
                    multiline
                    minRows={3}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <InputLabel>{t('form.fields.items')}</InputLabel>
              <ul className={styles.itemsList}>
                {fields.map((item, index) => (
                  <li key={item.id} className={styles.itemRow}>
                    <Grid container spacing={1}>
                      <Grid item xs={12}>
                        <Controller
                          name={`items.${index}.description` as const}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label={t('form.fields.itemDescription', { index: index + 1 })}
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`items.${index}.qty` as const}
                          control={control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              type="number"
                              label={t('form.fields.itemQty')}
                              fullWidth
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              inputProps={{ min: 0, step: 1 }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <Controller
                          name={`items.${index}.amount` as const}
                          control={control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              type="number"
                              label={t('form.fields.itemAmount')}
                              fullWidth
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              inputProps={{ min: 0, step: 0.01 }}
                              onBlur={(event) => {
                                field.onBlur();
                                const sanitized = normalizeNumberInput(event.target.value, 0, 2);
                                field.onChange(sanitized);
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Box display="flex" justifyContent="space-between" mt={1}>
                      {index === fields.length - 1 && (
                        <Button
                          variant="text"
                          onClick={() =>
                            append({ order: fields.length + 1, description: '', qty: 1, amount: 0 })
                          }
                        >
                          {t('form.actions.addItem')}
                        </Button>
                      )}
                      {fields.length > 1 && (
                        <Button variant="text" color="error" onClick={() => remove(index)}>
                          {t('form.actions.removeItem')}
                        </Button>
                      )}
                    </Box>
                  </li>
                ))}
              </ul>
            </Grid>

            <Grid item xs={12}>
              <div className={styles.twoColumnRow}>
                <Controller
                  name="discountType"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel htmlFor={discountTypeFieldId}>
                        {t('form.fields.discountType')}
                      </InputLabel>
                      <Select
                        {...field}
                        id={discountTypeFieldId}
                        value={field.value ?? DISCOUNT_TYPES.VALUE}
                        options={discountOptions}
                        fullWidth
                      />
                    </FormControl>
                  )}
                />
                <Controller
                  name="discountAmount"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      type="number"
                      label={t('form.fields.discountAmount')}
                      fullWidth
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      inputProps={{ min: 0, step: 0.01 }}
                      onBlur={(event) => {
                        field.onBlur();
                        const sanitized = normalizeNumberInput(event.target.value, 0, 2);
                        field.onChange(sanitized);
                      }}
                    />
                  )}
                />
              </div>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" className={styles.note}>
                {t('form.notes.discountHint')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <div className={styles.twoColumnRow}>
                <Controller
                  name="dueDate"
                  control={control}
                  render={({ field }) => (
                    <DueDateSelect
                      label={t('form.fields.dueDate')}
                      value={field.value ? new Date(field.value as number) : null}
                      onChange={(date) => field.onChange(date ? date.getTime() : null)}
                      defaultPreset={mode === 'create' ? '30_days' : 'none'}
                    />
                  )}
                />

                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel htmlFor={statusFieldId}>{t('form.fields.status')}</InputLabel>
                      <Select
                        {...field}
                        id={statusFieldId}
                        value={field.value ?? PROFORMA_INVOICE_STATUS.PENDING_PAYMENT}
                        options={statusOptions}
                        fullWidth
                        disabled={!isAdmin}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" flexDirection="column">
                <Controller
                  name="priceProposalOverrideFields"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox {...field} checked={field.value} disabled={!hasLinkedQuote} />
                      }
                      label={t('form.fields.overrideFields')}
                    />
                  )}
                />
                <Controller
                  name="priceProposalOverrideSettings"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox {...field} checked={field.value} disabled={!hasLinkedQuote} />
                      }
                      label={t('form.fields.overrideSettings')}
                    />
                  )}
                />
              </Box>
              <Typography variant="body2" className={styles.note}>
                {t('form.notes.overrideHint')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Box className={styles.pricingSummary}>
                <Typography variant="subtitle2">{t('form.fields.subtotalPrice')}</Typography>
                <Typography>{watch('subtotalPrice') ?? '-'}</Typography>
                <Typography variant="subtitle2">{t('form.fields.priceWithDiscount')}</Typography>
                <Typography>{watch('priceWithDiscount') ?? '-'}</Typography>
                <Typography variant="subtitle2">{t('form.fields.taxValue')}</Typography>
                <Typography>{watch('taxValue') ?? '-'}</Typography>
                <Typography variant="subtitle2">{t('form.fields.priceWithTax')}</Typography>
                <Typography>{watch('priceWithTax') ?? '-'}</Typography>
              </Box>
            </Grid>
          </Grid>
        </section>

        <Box display="flex" justifyContent="space-between" mt={4}>
          <Box display="flex">
            <Button variant="text" onClick={onCancel}>
              {t('form.actions.cancel')}
            </Button>
            {onDelete && (
              <Button
                variant="text"
                color="error"
                onClick={onDelete}
                disabled={formState.isSubmitting}
              >
                {t('duplicates:Delete_action')}
              </Button>
            )}
          </Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={formState.isSubmitting}
          >
            {mode === 'create' ? t('form.actions.create') : t('form.actions.update')}
          </Button>
        </Box>
      </form>
    </FormProvider>
  );

  const handleSettingsSubmit = async (values: SettingsFormValues) => {
    const entity = toSettingsEntity(values);
    initialSettingsRef.current = entity;
    resetWithSettings(entity);
    setShowDiscardDialog(false);
    setCurrentSection('main');
  };

  const renderSettingsForm = (
    <FormProvider {...settingsForm}>
      <div className={styles.formWrapper}>
        <div className={styles.settingsHeader}>
          <Button
            variant="text"
            className={styles.backButton}
            onClick={() => {
              if (settingsFormState.isDirty) {
                setShowDiscardDialog(true);
              } else {
                setCurrentSection('main');
              }
            }}
          >
            {t('form.actions.backToInvoice')}
          </Button>
        </div>
        <SettingsForm onSubmit={handleSettingsSubmit} />
      </div>
    </FormProvider>
  );

  return (
    <>
      {currentSection === 'main' ? renderMainForm : renderSettingsForm}
      {showDiscardDialog && (
        <DeleteDialog
          title={t('form.closeConfirm.title')}
          description={t('form.closeConfirm.description')}
          open={showDiscardDialog}
          onClose={() => setShowDiscardDialog(false)}
          onSuccess={() => {
            const entity = cloneSettingsForInvoice(defaultValues?.settings);
            initialSettingsRef.current = entity;
            resetWithSettings(entity);
            setShowDiscardDialog(false);
            setCurrentSection('main');
          }}
          cancelButtonText={t('form.closeConfirm.cancel')}
          deleteButtonText={t('form.closeConfirm.discard')}
        />
      )}
    </>
  );
}
